import * as React from "react";
import { Route, Routes, NavLink, Navigate, Link, Outlet } from "react-router-dom";
import { Button } from "@krix/ui";
import { useSession, authClient, isStaff } from "./lib/auth.ts";

const Dashboard = React.lazy(() => import("./pages/dashboard.tsx"));
const Users = React.lazy(() => import("./pages/users.tsx"));
const SeriesList = React.lazy(() => import("./pages/series-list.tsx"));
const SeriesEdit = React.lazy(() => import("./pages/series-edit.tsx"));
const SeriesEpisodes = React.lazy(() => import("./pages/series-episodes.tsx"));
const Categories = React.lazy(() => import("./pages/categories.tsx"));
const Settings = React.lazy(() => import("./pages/settings.tsx"));
const AuditLog = React.lazy(() => import("./pages/audit-log.tsx"));
const Login = React.lazy(() => import("./pages/login.tsx"));

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<RequireStaff />}>
        <Route element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route path="/users" element={<Users />} />
          <Route path="/series" element={<SeriesList />} />
          <Route path="/series/new" element={<SeriesEdit />} />
          <Route path="/series/:id" element={<SeriesEdit />} />
          <Route path="/series/:id/episodes" element={<SeriesEpisodes />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/audit-log" element={<AuditLog />} />
        </Route>
      </Route>
      <Route
        path="*"
        element={
          <div className="grid min-h-dvh place-items-center p-4 text-center">
            <div>
              <h1 className="text-3xl font-bold">404</h1>
              <Link className="mt-3 inline-block text-[var(--color-accent)]" to="/">
                Back to dashboard
              </Link>
            </div>
          </div>
        }
      />
    </Routes>
  );
}

function RequireStaff() {
  const { data, isPending } = useSession();
  if (isPending) return <LoadingScreen />;
  if (!data) return <Navigate to="/login" replace />;
  if (!isStaff(data.user.role)) {
    return (
      <div className="grid min-h-dvh place-items-center p-4 text-center">
        <div className="space-y-3">
          <h1 className="text-2xl font-bold">403 — staff only</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {data.user.email} doesn&apos;t have a staff role.
          </p>
          <Button onClick={() => authClient.signOut().then(() => location.reload())}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }
  return <Outlet />;
}

function Layout() {
  const { data } = useSession();
  const NAV = [
    { to: "/", label: "Dashboard", end: true },
    { to: "/users", label: "Users" },
    { to: "/series", label: "Series" },
    { to: "/categories", label: "Categories" },
    { to: "/audit-log", label: "Audit log" },
    { to: "/settings", label: "Settings" },
  ];
  return (
    <div className="grid min-h-dvh md:grid-cols-[220px_1fr]">
      <aside className="border-b border-[var(--color-border)] bg-[var(--color-card)] p-4 md:border-b-0 md:border-r">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" className="text-lg font-bold tracking-tight">
            Krix · Admin
          </Link>
          <span className="rounded-full bg-[var(--color-accent-soft)] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[var(--color-accent)]">
            staff
          </span>
        </div>
        <nav className="space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                [
                  "block rounded-md px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-[var(--color-secondary)] text-[var(--color-foreground)]"
                    : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-secondary)]/60 hover:text-[var(--color-foreground)]",
                ].join(" ")
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-8 border-t border-[var(--color-border)] pt-4 text-xs text-[var(--color-muted-foreground)]">
          <p>{data?.user.email}</p>
          <button
            className="mt-2 text-[var(--color-accent)] hover:underline"
            onClick={() => authClient.signOut().then(() => (location.href = "/login"))}
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="p-4 md:p-8">
        <React.Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </React.Suspense>
      </main>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--color-muted)] border-t-[var(--color-accent)]"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}
